/**
 * RenderAdapter - Base interface for render adapters.
 * 
 * A render adapter bridges the navigation system to a specific rendering approach.
 * Vidoriel (DOM patching), Canvas/WebGL (game loop), or anything else.
 * 
 * Projects pick one adapter. The navigation system calls it without knowing
 * how rendering works under the hood.
 */
export class RenderAdapter {

	/**
	 * Called once on initial page load.
	 * Receives the SSR bootstrap data and should set up the rendering system.
	 * 
	 * @param {object} ssrData - Server-side rendered bootstrap data (globalThis.ssr)
	 * @returns {object|null} The initial content reference (adapter-specific)
	 */
	async init(ssrData) {
		throw new Error('RenderAdapter.init() must be implemented');
	}

	/**
	 * Called on SPA navigation when a new route is loaded.
	 * 
	 * @param {object} response - The route response (from server or local resolver)
	 * @param {object} controller - The loaded controller instance with data merged in
	 */
	async update(response, controller) {
		throw new Error('RenderAdapter.update() must be implemented');
	}

	/**
	 * Called on cross-parent navigation when the entire chain root changes.
	 * Must tear down the current view and rebuild from scratch.
	 *
	 * @param {object} response - The full route response with chain data
	 * @param {object} controller - The loaded controller instance with data merged in
	 */
	async replaceChain(response, controller) {
		throw new Error('RenderAdapter.replaceChain() must be implemented');
	}

	/**
	 * Called when the current screen should be reloaded in place.
	 */
	async reload() {
		throw new Error('RenderAdapter.reload() must be implemented');
	}

	/**
	 * Called when the user navigates away from the current screen.
	 * Optional cleanup hook.
	 * 
	 * @param {object} nextController - The controller being navigated to
	 */
	async leave(nextController) {
		// Optional — adapters can override
	}

	resolveViewPath(component, viewsDir) {
		return null;
	}
}

export default RenderAdapter;
